import { SearchableSelect, SegmentedToggle, type TSearchableOption } from "@/ui/molecules";
import { cn } from "@/utils/utils";
import type { THomepageView } from "@/store";

/** "All" is a real segment rather than a deselected state — see SegmentedToggle. */
export type THomepageTypeFilter = "all" | "service_provider" | "portfolio_company";

const TYPE_OPTIONS: { value: THomepageTypeFilter; label: string }[] = [
    { value: "all", label: "All" },
    { value: "service_provider", label: "Service providers" },
    { value: "portfolio_company", label: "PortCo's" },
];

const BOARD_VIEW_OPTIONS: { value: THomepageView; label: string }[] = [
    { value: "accountant", label: "Accountant" },
    { value: "service_provider", label: "Service provider" },
];

export interface THomepageFiltersProps {
    /** Currently filtered submission, or null for "all submissions". */
    submission: TSearchableOption | null;
    /** Async lookup for the dropdown; scoped server-side to the caller's deals. */
    onSearchSubmissions: (query: string) => Promise<TSearchableOption[]>;
    onSubmissionChange: (option: TSearchableOption | null) => void;
    /** Business Partner filter. Omit the handlers to hide the dropdown entirely
     * — that is how the Service Provider board renders (see below). */
    businessPartner?: TSearchableOption | null;
    onSearchBusinessPartners?: (query: string) => Promise<TSearchableOption[]>;
    onBusinessPartnerChange?: (option: TSearchableOption | null) => void;
    /** Admin-only Type toggle and finance-team-member dropdown (§7.3). Omit the
     * handlers to hide them. */
    typeFilter?: THomepageTypeFilter;
    onTypeFilterChange?: (value: THomepageTypeFilter) => void;
    financeUser?: TSearchableOption | null;
    onSearchFinanceUsers?: (query: string) => Promise<TSearchableOption[]>;
    onFinanceUserChange?: (option: TSearchableOption | null) => void;
    /** Admin-only Board view toggle — which column set to render. Omit the
     * handler to hide it. */
    boardView?: THomepageView;
    onBoardViewChange?: (view: THomepageView) => void;
    className?: string;
}

/**
 * Board filter row (design doc §7.3).
 *
 * Which filters appear is driven by which handlers the caller passes, not by a
 * role check in here: the Service Provider board passes only the Submission
 * handlers, the Accountant board adds the Business Partner ones, and Admin adds
 * the Type toggle, the Board view toggle and the finance-team-member dropdown. There is deliberately
 * no Business Partner dropdown on the SP board — an SP employee belongs to
 * exactly one, so the filter would be meaningless (decision log §11).
 *
 * One dropdown covers both Service Providers and Portfolio Companies because
 * the board's `business_partner_id` filter matches either side of a deal. The
 * Type toggle is a different question — it filters on the *classification* a
 * Business Partner was given when it was created.
 */
export function HomepageFilters({
    submission,
    onSearchSubmissions,
    onSubmissionChange,
    businessPartner = null,
    onSearchBusinessPartners,
    onBusinessPartnerChange,
    typeFilter = "all",
    onTypeFilterChange,
    financeUser = null,
    onSearchFinanceUsers,
    onFinanceUserChange,
    boardView = "accountant",
    onBoardViewChange,
    className,
}: THomepageFiltersProps) {
    const showBusinessPartner =
        onSearchBusinessPartners !== undefined && onBusinessPartnerChange !== undefined;
    const showFinanceUser = onSearchFinanceUsers !== undefined && onFinanceUserChange !== undefined;

    return (
        <div className={cn("space-y-4 rounded-xl border bg-card p-4", className)}>
            {(onTypeFilterChange || onBoardViewChange) && (
                <div className="flex flex-wrap items-start gap-6">
                    {onTypeFilterChange && (
                        <div className="space-y-1.5">
                            <span className="block text-sm font-medium text-foreground">Type</span>
                            <SegmentedToggle
                                label="Type"
                                options={TYPE_OPTIONS}
                                value={typeFilter}
                                onChange={onTypeFilterChange}
                            />
                        </div>
                    )}
                    {onBoardViewChange && (
                        <div className="space-y-1.5">
                            <span className="block text-sm font-medium text-foreground">
                                Board view
                            </span>
                            <SegmentedToggle
                                label="Board view"
                                options={BOARD_VIEW_OPTIONS}
                                value={boardView}
                                onChange={onBoardViewChange}
                            />
                        </div>
                    )}
                </div>
            )}
            <div className="flex flex-wrap items-center gap-3">
                {showBusinessPartner && (
                    <div className="w-full sm:w-72">
                        <SearchableSelect
                            id="homepage-business-partner-filter"
                            value={businessPartner}
                            onSearch={onSearchBusinessPartners}
                            onSelect={onBusinessPartnerChange}
                            placeholder="All business partners"
                            searchOnOpen
                        />
                    </div>
                )}
                <div className="w-full sm:w-72">
                    <SearchableSelect
                        id="homepage-submission-filter"
                        value={submission}
                        onSearch={onSearchSubmissions}
                        onSelect={onSubmissionChange}
                        placeholder="All submissions"
                        // Opening the dropdown lists the caller's submissions
                        // without making them guess a name first.
                        searchOnOpen
                    />
                </div>
                {showFinanceUser && (
                    <div className="w-full sm:w-72">
                        <SearchableSelect
                            id="homepage-finance-user-filter"
                            value={financeUser}
                            onSearch={onSearchFinanceUsers}
                            onSelect={onFinanceUserChange}
                            placeholder="All finance team members"
                            searchOnOpen
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
